import React, { useState, useEffect, useRef } from "react";

const LockerButton = ({ children, offer }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [savedUsername, setSavedUsername] = useState(null);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showIframe, setShowIframe] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    let iframe = null;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    const handleMessage = (e) => {
      // If iframe posts a completion message, handle it.
      // You can refine origin check if you know the iframe origin.
      try {
        const data = typeof e.data === 'string' ? e.data : e.data?.type;
        if (data === 'og_offer_completed' || data === 'offer_completed') {
          // Close modal and redirect to stored offer
          const stored = sessionStorage.getItem('selected_offer');
          let id = stored ? (JSON.parse(stored).id || JSON.parse(stored).title) : 'locker-completed';
          setShowIframe(false);
          setIsOpen(false);
          setIsSubmitting(false);
          setProgress(0);
          // Hash router navigation
          window.location.hash = `#/offer/${encodeURIComponent(String(id))}`;
        }
      } catch (err) {
        // ignore malformed messages
      }
    };

    if (isOpen) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;

      window.addEventListener('message', handleMessage);
    }

    if (isOpen && showIframe) {
      // Remove any previous script if present
      const existingScript = document.getElementById("ogjs");
      if (existingScript) existingScript.remove();

      const container = document.getElementById("locker-container");
      if (container) {
        // Clear previous content
        container.innerHTML = "";

        // Create iframe that loads the locker view
        iframe = document.createElement("iframe");
        iframe.id = "og-iframe";
        iframe.src = "https://freebonusnow.store/cl/i/5nnlev";
        iframe.title = "Locker";
        iframe.setAttribute("class", "w-full h-full border-0");
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share");
        iframe.allowFullscreen = true;

        container.appendChild(iframe);
      } else {
        console.error("Locker container not found!");
      }

      // Optional: prevent redirect if adblock detection triggers
      window.ogblock = true;
    }

    return () => {
      // Cleanup injected iframe or script when modal closes or component unmounts
      if (iframe && iframe.parentElement) iframe.parentElement.removeChild(iframe);
      const existingScript = document.getElementById("ogjs");
      if (existingScript) existingScript.remove();

      // Restore previous body styles
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;

      // Clear any running progress interval
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      window.removeEventListener('message', handleMessage);
    };
  }, [isOpen, showIframe]);

  const startProgressAndLoad = (savedUsername) => {
    setIsSubmitting(true);
    setFormError("");
    setProgress(6);

    intervalRef.current = window.setInterval(() => {
      setProgress((prev) => {
        const incr = Math.floor(Math.random() * 20) + 8;
        const next = Math.min(prev + incr, 100);
        if (next >= 100) {
          if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsSubmitting(false);
          setShowIframe(true);
        }
        return next;
      });
    }, 350);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fullName.trim() || !username.trim()) {
      setFormError("Please enter your full name and a username.");
      return;
    }

    // Save username for this session (no persistent storage)
    setSavedUsername(username.trim());

    // Save selected offer (if provided) into sessionStorage so we can redirect later
    try {
      if (offer) {
        sessionStorage.setItem('selected_offer', JSON.stringify(offer));
      } else {
        sessionStorage.setItem('selected_offer', JSON.stringify({ id: 'locker', title: username.trim() }));
      }
    } catch (err) {
      // ignore sessionStorage errors
    }

    startProgressAndLoad(username.trim());
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setShowIframe(false);
    setIsSubmitting(false);
    setProgress(0);
  };

  const clearSavedUsername = () => {
    try {
      setUsername('');
      setSavedUsername(null);
    } catch (err) {
      console.warn('Could not clear localStorage');
    }
  };

  return (
    <div>
      <button
        onClick={openModal}
        aria-label="Browse new offers"
        className="w-full bg-[#28a745] hover:bg-green-600 text-white font-bold py-3 px-6 md:px-4 rounded-full shadow-2xl flex items-center justify-center gap-3 active:scale-95 transition-all"
      >
        {children ? (
          children
        ) : (
          <>
            <span className="text-lg">🎁</span>
            <span className="text-sm md:text-base">
              <span className="md:hidden">45 New Offers – Start Browsing</span>
              <span className="hidden md:inline">45 New Offers Added Today – Start Browsing</span>
            </span>
          </>
        )}
      </button>

      {isOpen && (
        <div id="locker-modal-overlay" className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
          <div role="dialog" aria-modal="true" className="bg-white p-6 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden relative shadow-2xl">
            <button
              className="absolute top-3 right-3 text-black font-bold text-xl"
              onClick={closeModal}
              aria-label="Close locker"
            >
              ✕
            </button>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-xl shadow-inner">🎁</div>
                <div>
                  <h3 className="text-lg font-bold">Claim Your Free Game Bonus</h3>
                  <p className="text-sm text-gray-600">Quickly join to unlock the locker. We only need a name and username.</p>
                </div>
              </div>

              {!showIframe ? (
                <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="space-y-3">
                  {savedUsername ? (
                    
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm text-gray-700">You're signed in as <span className="font-bold">{savedUsername}</span></p>
                        <p className="text-xs text-gray-500">Full name is optional if already saved.</p>
                      </div>
                      <button type="button" onClick={clearSavedUsername} className="text-xs text-indigo-500 underline">Change</button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="text-xs !text-gray-600">Full name</label>
                        <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-white text-sm text-black" placeholder="Jane Doe" />
                      </div>

                      <div>
                        <label className="text-xs !text-gray-600">Username</label>
                        <input value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-white text-sm text-black" placeholder="yourname123" />
                      </div>
                    </>
                  )}

                  {formError && <p className="text-xs text-red-500">{formError}</p>}

                  <div className="flex items-center gap-3">
                    <button type="submit" disabled={isSubmitting} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 rounded-lg">
                      {isSubmitting ? 'Preparing...' : 'Unlock Bonus'}
                    </button>
                    <button type="button" onClick={closeModal} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg">Cancel</button>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-green-400 transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </form>
              ) : (
                <div className="flex flex-col h-[60vh] w-full bg-gray-50 rounded-md overflow-hidden">
                  <div className="flex items-center justify-between p-3 border-b border-gray-200">
                    <div className="text-sm text-gray-700">If you've finished the offer, tap "I've completed" to continue.</div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          // Read stored offer and redirect
                          try {
                            const stored = sessionStorage.getItem('selected_offer');
                            let id = stored ? (JSON.parse(stored).id || JSON.parse(stored).title) : 'locker-completed';
                            setShowIframe(false);
                            setIsOpen(false);
                            setIsSubmitting(false);
                            setProgress(0);
                            window.location.hash = `#/offer/${encodeURIComponent(String(id))}`;
                          } catch (err) {
                            console.warn('Error reading session storage', err);
                          }
                        }}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-md text-sm"
                      >
                        I've completed
                      </button>

                      <button type="button" onClick={closeModal} className="bg-gray-200 text-gray-800 px-3 py-1.5 rounded-md text-sm">Cancel</button>
                    </div>
                  </div>

                  <div className="flex-1">
                    <div id="locker-container" className="w-full h-full" />
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default LockerButton;
